import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Heart,
  MapPin,
  Shield,
  Target,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const values = [
  {
    icon: Heart,
    title: "Simplicity first",
    desc: "Borrowing should feel effortless. We remove every unnecessary step so you can focus on what matters.",
  },
  {
    icon: Shield,
    title: "Transparency",
    desc: "No hidden fees, no fine print. You see your exact costs before you commit to anything.",
  },
  {
    icon: Users,
    title: "People over profit",
    desc: "We exist to serve Zambian borrowers — not to squeeze them. Fair rates, fair terms, always.",
  },
  {
    icon: Target,
    title: "Local focus",
    desc: "Built in Zambia, for Zambia. Our products, support, and payout methods are designed for the local market.",
  },
];

export default function AboutPage() {
  return (
    <div className="container mx-auto px-6 pb-20">
      {/* Hero */}
      <section className="mx-auto max-w-3xl pt-16 pb-12 text-center lg:pt-20">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">
          About us
        </p>
        <h1 className="text-3xl font-bold text-foreground font-display sm:text-4xl lg:text-5xl">
          Making borrowing{" "}
          <span className="bg-gradient-to-r from-primary to-emerald-600 bg-clip-text text-transparent">
            simple for Zambia
          </span>
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground leading-relaxed">
          Ngwees Capital is a Zambian fintech lending platform focused on giving
          everyday people access to fair, transparent, and fast personal loans.
        </p>
      </section>

      {/* Story */}
      <section className="mx-auto max-w-3xl">
        <div className="rounded-2xl border border-border/50 bg-white p-8 shadow-sm">
          <h2 className="text-xl font-bold text-foreground font-display mb-4">
            Our story
          </h2>
          <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
            <p>
              We started Ngwees because we saw how difficult and opaque
              borrowing was for most Zambians. Traditional lenders buried fees
              in fine print, took weeks to process applications, and required
              multiple branch visits just to get a small loan.
            </p>
            <p>
              We believed there had to be a better way. So we built a platform
              where you can apply from your phone, see exactly what you'll pay
              before you commit, and receive funds directly to your mobile money
              or bank account — all within 24 hours.
            </p>
            <p>
              Today, Ngwees serves borrowers across Zambia with personal loans
              from K10,000 to K1,000,000. We're proud to offer some of the most
              transparent and borrower-friendly terms in the market.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="mx-auto max-w-3xl pt-16">
        <h2 className="mb-8 text-center text-2xl font-bold text-foreground font-display">
          What we stand for
        </h2>
        <div className="grid gap-5 sm:grid-cols-2">
          {values.map((v) => (
            <div
              key={v.title}
              className="rounded-2xl border border-border/40 bg-white p-6 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <v.icon className="h-5 w-5" />
                </div>
                <h3 className="text-base font-semibold text-foreground">
                  {v.title}
                </h3>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {v.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Quick facts */}
      <section className="mx-auto max-w-3xl pt-16">
        <div className="rounded-2xl border border-border/50 bg-white p-8 shadow-sm">
          <h2 className="text-xl font-bold text-foreground font-display mb-6">
            Quick facts
          </h2>
          <div className="grid gap-6 sm:grid-cols-3 text-center">
            <div>
              <p className="text-3xl font-bold text-foreground font-display">K200k</p>
              <p className="mt-1 text-sm text-muted-foreground">Max loan amount</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-foreground font-display">24hrs</p>
              <p className="mt-1 text-sm text-muted-foreground">Average approval time</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-foreground font-display">ZMW</p>
              <p className="mt-1 text-sm text-muted-foreground">Zambia Kwacha only</p>
            </div>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="mx-auto max-w-3xl pt-16">
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4" />
          <span>Based in Lusaka, Zambia</span>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-3xl pt-12 text-center">
        <div className="rounded-3xl bg-foreground px-8 py-12 text-background shadow-2xl">
          <h2 className="text-2xl font-bold font-display sm:text-3xl">
            Ready to borrow the simple way?
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-background/70">
            Join borrowers across Zambia who chose transparency over fine print.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="h-12 rounded-xl bg-white px-8 text-foreground shadow-lg hover:bg-white/90"
            >
              <Link to="/register">
                Get started
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="h-12 rounded-xl border-white/25 px-8 text-white hover:bg-white/10 hover:text-white"
            >
              <Link to="/contact">Contact us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
